import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { apiDownload, apiRequest } from "../api/awsClient";
import ClearMemoryCard from "../components/ClearMemoryCard";
import FancySelect from "../components/FancySelect";
import "./Memory.css";

/*
 * Memory: what BossDrop has learned about how this team runs, as the literal,
 * structured text it reads before every decision (the server's document,
 * server/pm/teamMemory.js). Seen accumulating, not as a data viewer: the loop
 * at the top is what keeps it true, and a person can correct any line, which is
 * itself something it learns from. The memory is the customer's: it exports in
 * one click, and the page says so.
 *
 * Data: GET /api/memory (owner resolved server-side, person-level lines only
 * for admins and that person), POST /api/memory/correct, /api/memory/note,
 * /api/memory/confirm, /api/memory/retire, GET /api/memory/export. `api` is
 * injectable for tests and screenshots.
 */

const LOOKBACK_DAYS = 60;
const DAY_MS = 86400000;
const FEED_PAGE = 14;

const DEFAULT_API = { request: apiRequest, download: apiDownload };

const ERROR_TEXT = {
  unauthorized: "Your session has expired or you are not signed in. Sign in again and retry.",
  forbidden: "Only workspace admins can do that.",
  not_found: "That line is no longer there. Refresh to see the current memory.",
  not_correctable: "That line is set elsewhere. Change it where it is set.",
  text_required: "Write what is true instead.",
  wait_needs_duration: "That line needs a time in it, like 4 hours or 2 days.",
  unknown_section: "Pick where this belongs.",
  memory_failed: "The memory service returned an error.",
  memory_correct_failed: "BossDrop could not save that correction. Try again.",
  memory_note_failed: "BossDrop could not add that. Try again.",
  memory_export_failed: "BossDrop could not export its memory. Try again.",
  memory_confirm_failed: "BossDrop could not approve that.",
  memory_retire_failed: "BossDrop could not dismiss that.",
};

const SOURCE_SENTENCES = {
  consolidation: "BossDrop's routine pass over recent messages, tasks and CRM records",
  change_control: "BossDrop's watch on project scope",
  inbound_email: "an email sent to ai@bossdrop.ai",
};

const HEADLINE_FIELDS = ["summary", "statement", "decision", "note", "text", "description"];
const WHY_FIELDS = ["rationale", "why", "because", "reason"];
const HUMAN_REVIEW_KINDS = new Set(["policy", "decision"]);
const PROPOSED_SUFFIX = "#proposed";

const FEED_FILTERS = [
  { id: "all", label: "Everything" },
  { id: "learned", label: "Learned" },
  { id: "used", label: "Used" },
  { id: "people", label: "Your team" },
];
const FEED_GROUP = {
  new: "learned",
  changed: "learned",
  faded: "learned",
  used: "used",
  corrected: "people",
  added: "people",
  stopped: "people",
  restored: "people",
};

/* ------------------------------ small helpers ------------------------------ */

function asObject(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many || `${one}s`}`;
}

function count(n) {
  const x = Number(n);
  return Number.isFinite(x) && x > 0 ? Math.round(x) : 0;
}

function number(n) {
  return count(n).toLocaleString("en-US");
}

/** ISO-looking strings and Dates only: browsers parse "Sprint 12" into a date. */
function parseDate(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(v.trim())) return null;
  const s = v.trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(v) {
  const d = parseDate(v);
  if (!d) return "";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

function formatClock(v) {
  const d = parseDate(v);
  return d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
}

function dayLabel(v, now = new Date()) {
  const d = parseDate(v);
  if (!d) return "";
  if (d.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function relativeTime(v, now = Date.now()) {
  const d = parseDate(v);
  if (!d) return "";
  const abs = Math.abs(now - d.getTime());
  if (abs < 60000) return "just now";
  if (abs < 3600000) return `${plural(Math.floor(abs / 60000), "minute")} ago`;
  if (abs < DAY_MS) return `${plural(Math.floor(abs / 3600000), "hour")} ago`;
  const days = Math.floor(abs / DAY_MS);
  if (days === 1) return "yesterday";
  return days < 7 ? `${days} days ago` : `on ${formatDate(d)}`;
}

function daysSince(v, now = Date.now()) {
  const d = parseDate(v);
  return d ? Math.max(0, Math.floor((now - d.getTime()) / DAY_MS)) : 0;
}

function errorText(e) {
  const raw = String(e?.message || e || "").trim();
  if (ERROR_TEXT[raw]) return ERROR_TEXT[raw];
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return "Could not reach BossDrop. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
}

// Who did something, as a name: "dana.lee@acme.com" reads "Dana".
function actorName(email) {
  const first = String(email || "").split("@")[0].split(/[._+-]/)[0];
  return first ? first[0].toUpperCase() + first.slice(1) : "";
}

function firstText(obj, fields) {
  for (const field of fields) {
    const v = obj[field];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

/** A held memory's readable parts, out of its free-form value. */
function describeMemory(m) {
  const value = asObject(m.value);
  const provenance = asObject(m.provenance);
  const rationale = typeof provenance.rationale === "string" ? provenance.rationale.trim() : "";
  const head = firstText(value, HEADLINE_FIELDS);
  return {
    headline: head || (rationale ? `${m.key}: ${rationale}` : String(m.key || "")),
    why: firstText(value, WHY_FIELDS) || rationale,
  };
}

function reviewReasons(memory) {
  const prov = asObject(memory.provenance);
  const refs = Array.isArray(prov.sourceRefs) ? prov.sourceRefs.filter((r) => typeof r === "string" && r.trim()) : [];
  const conf = typeof memory.confidence === "number" && Number.isFinite(memory.confidence) ? memory.confidence : null;
  const out = [];
  if (!refs.length) {
    out.push("BossDrop cannot point to a message, task or record this came from, so there is nothing to check it against.");
  } else if (prov.untrusted === true) {
    out.push("This came from an email. BossDrop never files anything it learned from email without a person checking.");
  }
  if (conf === null) out.push("BossDrop never recorded how sure it was.");
  else if (conf < 0.8) out.push(`BossDrop was only ${Math.round(conf * 100)}% sure. It files nothing on its own below 80%.`);
  if (HUMAN_REVIEW_KINDS.has(memory.kind)) {
    out.push(
      memory.kind === "policy"
        ? "It is a standing rule that would change how BossDrop behaves everywhere, so a person always signs off."
        : "It is a decision BossDrop would then treat as settled, so a person always signs off.",
    );
  }
  return out.length ? out : ["It was flagged for a person to look at before BossDrop relies on it."];
}

function normalizePayload(json) {
  const j = asObject(json);
  const retro = asObject(j.retroActions);
  const team = asObject(j.teamMemory);
  const doc = asObject(team.document);
  return {
    canManage: !!j.canManage,
    memories: Array.isArray(j.memories) ? j.memories.filter((m) => m && m.kind && m.key) : [],
    retroItems: Array.isArray(retro.items) ? retro.items.filter(Boolean) : [],
    hasTeamMemory: !!j.teamMemory,
    document: {
      sections: Array.isArray(doc.sections) ? doc.sections : [],
      stopped: Array.isArray(doc.stopped) ? doc.stopped : [],
      hiddenLines: count(doc.hiddenLines),
      text: String(doc.text || ""),
      rewrittenAt: doc.rewrittenAt || null,
      lastRewrite: doc.lastRewrite || null,
      noteSections: Array.isArray(doc.noteSections) ? doc.noteSections : [],
    },
    stats: asObject(team.stats),
    progress: Array.isArray(team.progress) ? team.progress.filter((p) => p && p.text) : [],
    history: Array.isArray(team.history) ? team.history : [],
  };
}

function rewriteSummary(doc) {
  const last = asObject(doc.lastRewrite);
  const at = doc.rewrittenAt || last.at;
  if (!at) return "First rewrite after its next run.";
  const when = `${dayLabel(at)} ${formatClock(at)}`.trim();
  const parts = [];
  if (count(last.new)) parts.push(`${count(last.new)} new`);
  if (count(last.changed)) parts.push(`${count(last.changed)} changed`);
  return parts.length ? `${when}: ${parts.join(", ")}` : `${when}: nothing changed`;
}

/* ================================== page ================================== */

export default function Memory({ api = DEFAULT_API }) {
  const outlet = useOutletContext() || {};
  const { workspaceOwnerEmail = "", apiBase = "" } = outlet;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [reviewBusy, setReviewBusy] = useState({});
  const [reviewErrors, setReviewErrors] = useState({});

  const aliveRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const id = ++requestIdRef.current;
    setLoading(true);
    try {
      const json = await api.request(`/api/memory?lookbackDays=${LOOKBACK_DAYS}`);
      if (!aliveRef.current || id !== requestIdRef.current) return;
      setData(normalizePayload(json));
      setLoadError("");
    } catch (e) {
      if (!aliveRef.current || id !== requestIdRef.current) return;
      // A failed refresh keeps whatever was already on screen.
      setLoadError(errorText(e));
    } finally {
      if (aliveRef.current && id === requestIdRef.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setData(null);
    setReviewErrors({});
    load();
  }, [load, workspaceOwnerEmail]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const canManage = !!data?.canManage;
  const doc = data?.document;

  /** Correct, stop or restore a line; the server's answer is the new document. */
  const changeLine = useCallback(
    async (line, action, text = "") => {
      await api.request("/api/memory/correct", {
        method: "POST",
        body: { ref: line.ref, action, text },
      });
      if (!aliveRef.current) return;
      setNotice(
        action === "correct"
          ? "Saved. BossDrop reads your words from its next run, and they stay through every rewrite."
          : action === "stop"
            ? "BossDrop stopped using that line."
            : "Back to what BossDrop measured.",
      );
      await load();
    },
    [api, load],
  );

  const addNote = useCallback(
    async (section, text) => {
      await api.request("/api/memory/note", { method: "POST", body: { section, text } });
      if (!aliveRef.current) return;
      setNotice("Added. BossDrop reads it from its next run.");
      await load();
    },
    [api, load],
  );

  const review = useCallback(
    async (memory, action) => {
      const id = `${memory.kind}:${memory.key}`;
      setReviewBusy((b) => ({ ...b, [id]: action }));
      setReviewErrors((errs) => ({ ...errs, [id]: "" }));
      try {
        await api.request(action === "confirm" ? "/api/memory/confirm" : "/api/memory/retire", {
          method: "POST",
          body: { kind: memory.kind, key: memory.key },
        });
        if (!aliveRef.current) return;
        setNotice(action === "confirm" ? "BossDrop will use that from now on." : "Dropped. BossDrop will not raise it again.");
        await load();
      } catch (e) {
        if (aliveRef.current) setReviewErrors((errs) => ({ ...errs, [id]: errorText(e) }));
      } finally {
        if (aliveRef.current) {
          setReviewBusy((b) => {
            const next = { ...b };
            delete next[id];
            return next;
          });
        }
      }
    },
    [api, load],
  );

  const exportMemory = useCallback(
    async (format) => {
      try {
        const blob = await api.download(`/api/memory/export?format=${format}`);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = format === "json" ? "bossdrop-memory.json" : "bossdrop-memory.txt";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        if (aliveRef.current) setLoadError(errorText(e));
      }
    },
    [api],
  );

  const copyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(doc?.text || "");
      setNotice("Copied the memory as plain text.");
    } catch {
      setLoadError("Your browser would not let BossDrop copy that. Use Export instead.");
    }
  }, [doc?.text]);

  const pending = useMemo(
    () => (data?.memories || []).filter((m) => m.status === "pending_review"),
    [data],
  );

  function replacedBy(m) {
    const base = String(m?.provenance?.revises || "");
    if (!base || !String(m.key || "").endsWith(PROPOSED_SUFFIX)) return "";
    const current = (data?.memories || []).find((x) => x.kind === m.kind && x.key === base);
    return current ? describeMemory(current).headline : base;
  }

  return (
    <section className="panel controlRoom memoryPage">
      <header className="memHeader">
        <div>
          <h2>Memory</h2>
          <p className="muted memLede">
            What BossDrop has learned about how your team runs. It is rewritten every day from real work, and it is the
            exact text BossDrop reads before every decision.
          </p>
        </div>
      </header>

      {loadError ? (
        <div className="memBanner" role="alert">
          <span>{loadError}</span>
          {data ? (
            <button type="button" className="btn memBtnSmall" onClick={load} disabled={loading}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}
      {notice ? (
        <div className="memBanner isInfo" role="status">
          {notice}
        </div>
      ) : null}

      {!data ? (
        loading ? (
          <div className="memLoading">
            <span className="memSpinner" aria-hidden="true" />
            Reading what BossDrop remembers…
          </div>
        ) : (
          <div className="memEmptyRow">
            <button type="button" className="btn" onClick={load}>
              Try again
            </button>
          </div>
        )
      ) : (
        <>
          <TheLoop stats={data.stats} doc={doc} />

          {canManage && pending.length ? (
            <section className="memWaiting" aria-label="Waiting for you">
              <h3 className="memCardTitle">
                Waiting for you <span className="memCount">{pending.length}</span>
              </h3>
              <p className="muted memCardNote">
                Things people said that BossDrop wants a person to check before it relies on them.
              </p>
              <div className="memWaitingList">
                {pending.map((m) => {
                  const id = `${m.kind}:${m.key}`;
                  return (
                    <WaitingCard
                      key={id}
                      memory={m}
                      replaces={replacedBy(m)}
                      canManage={canManage}
                      busyAction={reviewBusy[id]}
                      error={reviewErrors[id]}
                      onAction={review}
                    />
                  );
                })}
              </div>
            </section>
          ) : null}

          <div className="memLayout">
            <div className="memMain">
              <MemoryDocument
                doc={doc}
                progress={data.progress}
                canManage={canManage}
                unavailable={!data.hasTeamMemory}
                onChange={changeLine}
                onAdd={addNote}
                onCopy={copyText}
                onExport={exportMemory}
              />
            </div>
            <aside className="memRail">
              <WhatChanged history={data.history} />
              {data.progress.length ? <StillLearning progress={data.progress} /> : null}
              <ItsYours canManage={canManage} onExport={exportMemory} />
              {data.retroItems.length ? <OpenFromRetros items={data.retroItems} /> : null}
            </aside>
          </div>

          {canManage ? (
            <ClearMemoryCard apiBase={apiBase} canManageWorkspace={canManage} onCleared={load} />
          ) : null}
        </>
      )}
    </section>
  );
}

/* ------------------------------- the loop -------------------------------- */

/** The moat, shown rather than claimed: it watches, rewrites, acts, and learns from you. */
function TheLoop({ stats, doc }) {
  const s = asObject(stats);
  const week = asObject(s.thisWeek);
  const since = formatDate(s.learningSince);
  const days = daysSince(s.learningSince);
  const steps = [
    {
      label: "Watches real work",
      value: number(s.moments),
      unit: count(s.moments) === 1 ? "moment seen" : "moments seen",
      note: since
        ? `${number(s.weekMoments)} this week. ${days ? `Learning for ${plural(days, "day")}, since ${since}.` : `Learning since ${since}.`}`
        : "Starts with its next run.",
    },
    {
      label: "Rewrites this every day",
      value: number(s.linesLearned),
      unit: count(s.linesLearned) === 1 ? "thing learned" : "things learned",
      note: rewriteSummary(doc),
    },
    {
      label: "Changes what it does",
      value: number(week.used),
      unit: count(week.used) === 1 ? "decision used it this week" : "decisions used it this week",
      note: count(s.usedTotal) ? `${number(s.usedTotal)} in all.` : "Decisions cite the lines they use.",
    },
    {
      label: "Learns from you",
      value: number(s.corrections),
      unit: count(s.corrections) === 1 ? "line in your words" : "lines in your words",
      note: "Your words outrank anything measured.",
    },
  ];
  return (
    <ol className="memLoop" aria-label="How BossDrop's memory stays true">
      {steps.map((step) => (
        <li className="memLoopStep" key={step.label}>
          <div className="memLoopLabel">{step.label}</div>
          <div className="memLoopValue">{step.value}</div>
          <div className="memLoopUnit">{step.unit}</div>
          <div className="memLoopNote">{step.note}</div>
        </li>
      ))}
    </ol>
  );
}

/* ----------------------------- the document ------------------------------ */

function emptySectionText(section, progress) {
  const note = progress.find((p) => p.area === section.id);
  if (note) return note.text;
  switch (section.id) {
    case "worked":
      return "Nothing to judge yet. BossDrop checks a few days after each move whether it got a result.";
    case "facts":
      return "Nothing yet. Durable things your team says land here.";
    case "weeks":
      return "The first summary comes at the end of the week.";
    default:
      return "Nothing here yet.";
  }
}

function MemoryDocument({ doc, progress, canManage, unavailable, onChange, onAdd, onCopy, onExport }) {
  const [editing, setEditing] = useState("");
  return (
    <section className="memDoc" aria-label="BossDrop's memory, as plain text">
      <div className="memDocBar">
        <div className="memDocHead">
          <h3 className="memDocTitle">How this team runs</h3>
          <div className="memDocStamp">
            {doc.rewrittenAt ? `Rewritten ${relativeTime(doc.rewrittenAt)}` : "Not rewritten yet"}
          </div>
        </div>
        <div className="memDocActions">
          <button type="button" className="btn memBtnSmall" onClick={onCopy} disabled={!doc.text}>
            Copy
          </button>
          {canManage ? (
            <>
              <button type="button" className="btn memBtnSmall" onClick={() => onExport("text")}>
                Export text
              </button>
              <button type="button" className="btn memBtnSmall" onClick={() => onExport("json")}>
                Export JSON
              </button>
            </>
          ) : null}
        </div>
      </div>

      {unavailable ? (
        <p className="memEmpty memDocUnavailable">BossDrop could not read this just now. Refresh in a minute.</p>
      ) : (
        <div className="memDocBody">
          {doc.sections.map((section) => (
            <section className="memSec" key={section.id} aria-label={section.title}>
              <h4 className="memSecTitle">{section.title}</h4>
              <p className="memSecBlurb">{section.blurb}</p>
              {section.lines.length ? (
                <ul className="memLines">
                  {section.lines.map((line) => (
                    <MemoryLine
                      key={line.ref}
                      line={line}
                      canManage={canManage}
                      editing={editing === line.ref}
                      onEdit={() => setEditing(line.ref)}
                      onClose={() => setEditing("")}
                      onChange={onChange}
                    />
                  ))}
                </ul>
              ) : (
                <p className="memSecEmpty">{emptySectionText(section, progress)}</p>
              )}
            </section>
          ))}
          {doc.hiddenLines ? (
            <p className="memSecEmpty memHidden">
              {plural(doc.hiddenLines, "line")} about other people's timing {doc.hiddenLines === 1 ? "is" : "are"} only
              shown to admins and to them.
            </p>
          ) : null}
        </div>
      )}

      {canManage ? (
        <div className="memDocFoot">
          <AddLine sections={doc.noteSections} onAdd={onAdd} />
          {doc.stopped.length ? <StoppedLines lines={doc.stopped} onChange={onChange} /> : null}
        </div>
      ) : null}
    </section>
  );
}

function evidenceText(line) {
  const n = count(line.evidence?.n);
  const since = line.evidence?.since ? formatDate(line.evidence.since) : "";
  if (!n && !since) return "";
  return `Based on ${n ? plural(n, "moment") : "real work"}${since ? ` since ${since}` : ""}.`;
}

function MemoryLine({ line, canManage, editing, onEdit, onClose, onChange }) {
  const mine = !!(line.corrected || line.added);
  const used = count(line.usedThisWeek);
  return (
    <li className={`memLine${mine ? " isYours" : ""}${line.inBrief === false ? " isOutOfBrief" : ""}`}>
      <div className="memLineRow">
        <span className="memLineDash" aria-hidden="true">
          -
        </span>
        <div className="memLineText" title={evidenceText(line) || undefined}>
          {line.plain || line.text}
        </div>
        <div className="memLineMeta">
          {line.isNew ? <span className="memTag isNew">New</span> : null}
          {used ? (
            <span className="memTag isUsed" title={line.lastUsedAt ? `Last used ${relativeTime(line.lastUsedAt)}` : undefined}>
              Used {plural(used, "time")} this week
            </span>
          ) : null}
          {line.inBrief === false ? (
            <span className="memTag" title="Kept, but it did not fit in what BossDrop reads before each decision.">
              Not in today's brief
            </span>
          ) : null}
          {line.editAt ? (
            <Link className="memLineAction" to={line.editAt}>
              Set in {line.editLabel || "Control Room"}
            </Link>
          ) : canManage && line.correctable ? (
            <button type="button" className="memLineAction" onClick={onEdit} aria-label={`Correct: ${line.text}`}>
              Correct
            </button>
          ) : null}
        </div>
      </div>
      {editing ? <CorrectionEditor line={line} onClose={onClose} onChange={onChange} /> : null}
    </li>
  );
}

/** One control to correct a line: the correction is itself something it learns from. */
function CorrectionEditor({ line, onClose, onChange }) {
  const [text, setText] = useState(line.text);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const run = async (action) => {
    setBusy(action);
    setError("");
    try {
      await onChange(line, action, action === "correct" ? text : "");
      onClose();
    } catch (e) {
      setError(errorText(e));
      setBusy("");
    }
  };
  const isNote = line.source === "note";
  return (
    <form
      className="memFix"
      onSubmit={(e) => {
        e.preventDefault();
        run("correct");
      }}
    >
      <label className="memFixLabel" htmlFor={`fix-${line.ref}`}>
        {isNote ? "Your line" : "What is true instead?"}
      </label>
      <textarea
        id={`fix-${line.ref}`}
        className="memFixInput"
        rows={2}
        maxLength={400}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
      />
      <p className="memFixHint">
        {line.needsDuration
          ? "Say how long, like 4 hours or 2 days (a working day is 8 hours). This becomes your team's rule: BossDrop waits that long unless the work is critical."
          : "BossDrop reads your words from its next run, they outrank anything it measures, and they stay through every rewrite."}
      </p>
      {line.measuredText ? <p className="memFixHint">BossDrop measured: {line.measuredText}</p> : null}
      {evidenceText(line) ? <p className="memFixHint">{evidenceText(line)}</p> : null}
      {error ? (
        <p className="memFixError" role="alert">
          {error}
        </p>
      ) : null}
      <div className="memFixActions">
        <button type="submit" className="btn primary memBtnSmall" disabled={!!busy || !text.trim()}>
          {busy === "correct" ? "Saving…" : "Save correction"}
        </button>
        <button type="button" className="btn memBtnSmall" onClick={onClose} disabled={!!busy}>
          Cancel
        </button>
        <span className="memFixSpacer" />
        {line.corrected ? (
          <button type="button" className="btn memBtnSmall" onClick={() => run("restore")} disabled={!!busy}>
            Go back to what it measured
          </button>
        ) : null}
        <button type="button" className="btnDangerGhost memBtnSmall" onClick={() => run("stop")} disabled={!!busy}>
          {isNote ? "Remove this line" : "Stop using this"}
        </button>
      </div>
    </form>
  );
}

function AddLine({ sections, onAdd }) {
  const options = sections.map((s) => ({ value: s.id, label: s.title }));
  const [section, setSection] = useState("policies");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      await onAdd(section, text);
      setText("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="memAdd" onSubmit={submit}>
      <label className="memFixLabel" htmlFor="memAddInput">
        Tell BossDrop something it should know
      </label>
      <div className="memAddRow">
        <input
          id="memAddInput"
          className="inputFancy memAddInput"
          maxLength={400}
          placeholder="Client calls on Fridays are never moved."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {options.length ? (
          <div className="memAddSection">
            <FancySelect value={section} onChange={setSection} options={options} ariaLabel="Where this belongs" usePortal={true} />
          </div>
        ) : null}
        <button type="submit" className="btn memBtnSmall" disabled={busy || !text.trim()}>
          {busy ? "Adding…" : "Add"}
        </button>
      </div>
      {error ? (
        <p className="memFixError" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function StoppedLines({ lines, onChange }) {
  const [error, setError] = useState("");
  return (
    <details className="memStopped">
      <summary>
        {plural(lines.length, "line")} BossDrop stopped using
      </summary>
      <ul>
        {lines.map((line) => (
          <li key={line.ref}>
            <span>{line.text}</span>
            <button
              type="button"
              className="memLineAction"
              onClick={() => onChange(line, "restore").catch((e) => setError(errorText(e)))}
            >
              Use it again
            </button>
          </li>
        ))}
      </ul>
      {error ? (
        <p className="memFixError" role="alert">
          {error}
        </p>
      ) : null}
    </details>
  );
}

/* --------------------------------- the rail -------------------------------- */

function eventLabel(e) {
  const who = actorName(e.actor);
  switch (e.kind) {
    case "new":
      return "Learned";
    case "changed":
      return "Updated";
    case "faded":
      return "Faded, not enough recent evidence";
    case "used":
      return "Used in a decision";
    case "corrected":
      return who ? `Corrected by ${who}` : "Corrected";
    case "added":
      return who ? `Added by ${who}` : "Added";
    case "stopped":
      return who ? `Stopped by ${who}` : "Stopped using";
    case "restored":
      return who ? `Restored by ${who}` : "Restored";
    default:
      return "";
  }
}

function FeedItem({ event }) {
  const main = event.kind === "faded" || event.kind === "stopped" ? event.before : event.after || event.before;
  const was = event.kind === "changed" || event.kind === "corrected" ? event.before : "";
  const because = event.kind === "used" ? event.before : "";
  return (
    <li className={`memFeedItem is-${event.kind}`}>
      <span className="memFeedDot" aria-hidden="true" />
      <div>
        <div className="memFeedLabel">
          {eventLabel(event)}
          <span className="memFeedTime">{formatClock(event.at)}</span>
        </div>
        <div className="memFeedText">{main}</div>
        {was ? <div className="memFeedSub">Was: {was}</div> : null}
        {because ? <div className="memFeedSub isBecause" title={because}>Because: {because}</div> : null}
      </div>
    </li>
  );
}

function WhatChanged({ history }) {
  const [filter, setFilter] = useState("all");
  const [shown, setShown] = useState(FEED_PAGE);
  const visible = history.filter((e) => eventLabel(e) && (filter === "all" || FEED_GROUP[e.kind] === filter));
  const days = [];
  for (const event of visible.slice(0, shown)) {
    const day = dayLabel(event.at);
    const last = days[days.length - 1];
    if (last?.day === day) last.events.push(event);
    else days.push({ day, events: [event] });
  }
  return (
    <section className="memRailCard" aria-label="What changed">
      <h3 className="memCardTitle">What changed</h3>
      <div className="memFeedFilters" role="group" aria-label="Show">
        {FEED_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`chip memFeedFilter${filter === f.id ? " isActive" : ""}`}
            aria-pressed={filter === f.id}
            onClick={() => {
              setFilter(f.id);
              setShown(FEED_PAGE);
            }}
          >
            {f.label}
          </button>
        ))}
      </div>
      {days.length ? (
        days.map(({ day, events }) => (
          <div className="memFeedDay" key={day}>
            <div className="memFeedDayLabel">{day}</div>
            <ul className="memFeed">
              {events.map((event) => (
                <FeedItem key={event.id} event={event} />
              ))}
            </ul>
          </div>
        ))
      ) : (
        <p className="memEmpty">
          {filter === "all"
            ? "Nothing yet. What BossDrop learns shows up here as it happens."
            : "Nothing of this kind yet."}
        </p>
      )}
      {visible.length > shown ? (
        <button type="button" className="memLineAction memFeedMore" onClick={() => setShown((n) => n + FEED_PAGE)}>
          Show more
        </button>
      ) : null}
    </section>
  );
}

function StillLearning({ progress }) {
  return (
    <section className="memRailCard" aria-label="Still learning">
      <h3 className="memCardTitle">Still learning</h3>
      <p className="muted memCardNote">BossDrop only says what it has seen enough times to mean something.</p>
      <ul className="memLearning">
        {progress.map((p) => (
          <li key={p.text}>{p.text}</li>
        ))}
      </ul>
    </section>
  );
}

function ItsYours({ canManage, onExport }) {
  return (
    <section className="memRailCard" aria-label="This memory is yours">
      <h3 className="memCardTitle">It's yours</h3>
      <p className="memYours">
        Export all of it any time and take it with you. What stays here is the loop that keeps it true: watching real
        work, rewriting this every day, and changing what BossDrop does tomorrow.
      </p>
      {canManage ? (
        <div className="memYoursActions">
          <button type="button" className="btn memBtnSmall" onClick={() => onExport("text")}>
            Export text
          </button>
          <button type="button" className="btn memBtnSmall" onClick={() => onExport("json")}>
            Export JSON
          </button>
        </div>
      ) : (
        <p className="muted memCardNote">A workspace admin can export it.</p>
      )}
    </section>
  );
}

function OpenFromRetros({ items }) {
  return (
    <section className="memRailCard" aria-label="Open from past retros">
      <h3 className="memCardTitle">
        Open from past retros <span className="memCount">{items.length}</span>
      </h3>
      <ul className="memRetros">
        {items.slice(0, 8).map((item) => (
          <li key={item.key || item.title}>
            <div className="memRetroTitle">{item.title || "Untitled action item"}</div>
            <div className="memFeedSub">
              {[item.ownerEmail ? actorName(item.ownerEmail) : "No owner", item.dueDate ? `due ${formatDate(item.dueDate)}` : ""]
                .filter(Boolean)
                .join(", ")}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------ waiting for you ----------------------------- */

function WaitingCard({ memory, replaces, canManage, busyAction, error, onAction }) {
  const d = describeMemory(memory);
  const source = String(memory.source || "");
  const from = SOURCE_SENTENCES[source] || "a source BossDrop did not name";
  return (
    <article className="memWait">
      <div className="memWaitText">{d.headline}</div>
      {replaces ? <div className="memFeedSub">Currently: {replaces}. It keeps using this until you say yes.</div> : null}
      <details className="memWaitWhy">
        <summary>Why it is asking</summary>
        <p>From {from}.</p>
        {d.why ? <p>Its reasoning: {d.why}</p> : null}
        <ul>
          {reviewReasons(memory).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </details>
      {canManage ? (
        <div className="memWaitActions">
          <button
            type="button"
            className="btn success memBtnSmall"
            disabled={!!busyAction}
            onClick={() => onAction(memory, "confirm")}
          >
            {busyAction === "confirm" ? "Saving…" : "Yes, use this"}
          </button>
          <button
            type="button"
            className="btn memBtnSmall"
            disabled={!!busyAction}
            onClick={() => onAction(memory, "dismiss")}
          >
            {busyAction === "dismiss" ? "Dropping…" : "No, forget it"}
          </button>
        </div>
      ) : null}
      {error ? (
        <p className="memFixError" role="alert">
          {error}
        </p>
      ) : null}
    </article>
  );
}
